import {
  computed,
  inject,
  onBeforeUnmount,
  provide,
  ref,
  shallowRef,
  type InjectionKey,
} from 'vue'

import { useRouter } from 'vue-router'

import { DnaCaptureMode, FileType } from '@/shared/graphql/router/graphql'
import { useUserStore } from '@/shared/stores/user'
import { closeAllDialogs, openDialog, Dialog } from '@/shared/dialogs/dialog'
import { closeAllModals } from '@/shared/dialogs/modals'
import { closeAllSheets } from '@/shared/dialogs/sheets'

import { useInfringingFileDetails } from '../match/useInfringingFileDetails'
import type { LocalWork } from '../match/InfringingWorkDetails.vue'

import { CheckAborted, checkFile } from './checkFile'
import { mergeFindings, sortFindings, type Finding } from './findings'
import { LiveCheck, SAMPLE_RATE, type LiveSnapshot } from './liveCheck'
import { checkedMaterial } from './material'
import { createCheckerReport } from './report'
import type { CheckerResults, CheckerSource } from './results'

export type CheckerMode = 'audio' | 'visual' | 'upload'

export type CheckerStep =
  | 'capture'
  | 'empty'
  | 'list'
  | 'match'
  | 'confirm'
  | 'details'

export type PermissionState = 'unknown' | 'granted' | 'denied'

export type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading'; name: string; percentage: number }
  | { kind: 'analyzing' }

const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
}

const isPermissionError = (error: unknown) =>
  error instanceof DOMException &&
  (error.name === 'NotAllowedError' || error.name === 'SecurityError')

const isDeviceBusy = (error: unknown) =>
  error instanceof DOMException &&
  (error.name === 'NotReadableError' || error.name === 'AbortError')

const CAPTURE_TYPE: Record<CheckerSource, string> = {
  visual: 'Visual capture',
  audio: 'Audio recording',
  file: 'Uploaded file',
}

const queryPermission = async (
  name: 'microphone' | 'camera'
): Promise<PermissionStatus | null> => {
  try {
    return await navigator.permissions.query({
      name: name as PermissionName,
    })
  } catch {
    return null
  }
}

export const createChecker = () => {
  const userStore = useUserStore()
  const router = useRouter()

  const mode = ref<CheckerMode>('audio')
  const step = ref<CheckerStep>('capture')
  const results = shallowRef<CheckerResults | null>(null)
  const emptySource = ref<CheckerSource>('audio')
  const selected = shallowRef<Finding | null>(null)
  const live = shallowRef<LiveSnapshot | null>(null)
  const requesting = ref(false)
  const error = ref<string | null>(null)
  const microphone = ref<PermissionState>('unknown')
  const camera = ref<PermissionState>('unknown')
  const cameraStream = shallowRef<MediaStream | null>(null)
  const upload = ref<UploadState>({ kind: 'idle' })
  const creating = ref<number | null>(null)
  const createError = ref<string | null>(null)
  const pcmListeners = new Set<(pcm: Uint8Array, rate: number) => void>()

  let check: LiveCheck | null = null
  let audioStream: MediaStream | null = null
  let uploadAbort: AbortController | null = null
  let video: HTMLVideoElement | null = null
  let disposed = false
  const permissionWatchers: PermissionStatus[] = []

  const credits = computed(() => userStore.user?.reportCredits)
  const { file: details } = useInfringingFileDetails(
    () => results.value?.matchIds[0]
  )
  const localWork = computed<LocalWork | null>(() => {
    const current = results.value
    if (!current) return null
    const material = checkedMaterial(current)
    return {
      name: material.name,
      type: current.source === 'visual' ? FileType.Video : material.fileType,
      previewUrl: material.src,
      captureType: CAPTURE_TYPE[current.source],
      recordedAt: current.recordedAt,
      durationSeconds: current.durationSeconds,
      media: current.media,
    }
  })
  const busy = computed(
    () =>
      requesting.value ||
      (live.value !== null && live.value.phase !== 'idle') ||
      upload.value.kind !== 'idle'
  )
  const findingCount = computed(() => live.value?.findings.size ?? 0)
  const listening = computed(() => live.value?.phase === 'recording')
  const capturing = computed(
    () => live.value?.phase === 'recording' || live.value?.phase === 'draining'
  )
  const glowMode = computed<'idle' | 'requesting' | 'microphone'>(() =>
    requesting.value ||
    live.value?.phase === 'starting' ||
    live.value?.phase === 'waiting'
      ? 'requesting'
      : live.value?.phase === 'recording'
        ? 'microphone'
        : 'idle'
  )

  const watchPermission = async (
    name: 'microphone' | 'camera',
    target: typeof microphone
  ) => {
    const status = await queryPermission(name)
    if (!status || disposed) return
    const apply = () => {
      if (status.state === 'denied') target.value = 'denied'
      else if (status.state === 'granted') target.value = 'granted'
      else if (target.value === 'denied') target.value = 'unknown'
    }
    apply()
    status.onchange = () => {
      apply()
      if (
        name === 'camera' &&
        status.state === 'granted' &&
        mode.value === 'visual'
      )
        void openCamera()
    }
    permissionWatchers.push(status)
  }

  void watchPermission('microphone', microphone)
  void watchPermission('camera', camera)

  const stopTracks = (stream: MediaStream | null) =>
    stream?.getTracks().forEach((track) => track.stop())

  const openCamera = async () => {
    if (cameraStream.value || disposed) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
      })
      if (disposed || mode.value !== 'visual') {
        stopTracks(stream)
        return
      }
      camera.value = 'granted'
      cameraStream.value = stream
    } catch (failure) {
      if (isPermissionError(failure)) camera.value = 'denied'
      else if (isDeviceBusy(failure))
        error.value =
          'Another application is using the camera. Close it, then try again.'
      else error.value = 'The camera couldn’t be started.'
    }
  }

  const closeCamera = () => {
    stopTracks(cameraStream.value)
    cameraStream.value = null
  }

  const setVideo = (element: HTMLVideoElement | null) => {
    video = element
  }

  const setMode = (next: CheckerMode) => {
    if (busy.value || next === mode.value) return
    error.value = null
    mode.value = next
    if (next === 'visual') void openCamera()
    else closeCamera()
  }

  const showResults = (next: CheckerResults) => {
    closeCamera()
    if (!next.findings.length) {
      emptySource.value = next.source
      results.value = next
      step.value = 'empty'
      return
    }
    results.value = {
      ...next,
      findings: sortFindings(mergeFindings(new Map(), next.findings).values()),
    }
    if (results.value.findings.length === 1) {
      selected.value = results.value.findings[0]
      step.value = 'match'
      return
    }
    step.value = 'list'
  }

  const handleLive = (snapshot: LiveSnapshot) => {
    if (snapshot.phase === 'finished') {
      check = null
      live.value = null
      stopTracks(audioStream)
      audioStream = null
      if (snapshot.results) showResults(snapshot.results)
      return
    }
    if (snapshot.phase === 'failed' || snapshot.phase === 'idle') {
      check = null
      live.value = null
      stopTracks(audioStream)
      audioStream = null
      if (snapshot.error) error.value = snapshot.error
      return
    }
    live.value = snapshot
  }

  const startLive = async (visual: boolean) => {
    if (check || requesting.value) return
    error.value = null
    requesting.value = true
    try {
      audioStream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONSTRAINTS,
      })
      microphone.value = 'granted'
    } catch (failure) {
      requesting.value = false
      if (isPermissionError(failure)) microphone.value = 'denied'
      else if (isDeviceBusy(failure))
        error.value =
          'Another application is using the microphone. Close it, then try again.'
      else error.value = 'The microphone couldn’t be started.'
      return
    }
    requesting.value = false
    if (disposed) {
      stopTracks(audioStream)
      return
    }
    if (visual && !video) {
      stopTracks(audioStream)
      return
    }
    check = new LiveCheck(
      visual ? DnaCaptureMode.Camera : DnaCaptureMode.Microphone,
      audioStream,
      handleLive,
      (pcm, rate) => pcmListeners.forEach((listener) => listener(pcm, rate)),
      visual && video ? video : undefined
    )
    void check.start()
  }

  const toggleLive = (visual: boolean) => {
    if (check) void check.finish()
    else void startLive(visual)
  }

  const review = () => check?.review()

  const pickFile = async (file: File) => {
    error.value = null
    const controller = new AbortController()
    uploadAbort = controller
    upload.value = { kind: 'uploading', name: file.name, percentage: 0 }
    try {
      const result = await checkFile(
        file,
        (phase) => {
          upload.value =
            phase.kind === 'analyzing'
              ? phase
              : {
                  kind: 'uploading',
                  name: file.name,
                  percentage: phase.percentage,
                }
        },
        controller.signal
      )
      upload.value = { kind: 'idle' }
      showResults({
        source: 'file',
        findings: result.findings,
        media: file,
        peaks: [],
        frames: [],
        matchIds: result.infringementMatchIds ?? [],
      })
    } catch (failure) {
      if (failure instanceof CheckAborted) return
      upload.value = { kind: 'idle' }
      error.value = failure instanceof Error ? failure.message : null
    } finally {
      if (uploadAbort === controller) uploadAbort = null
    }
  }

  const openMatch = (finding: Finding) => {
    selected.value = finding
    createError.value = null
    step.value = 'match'
  }

  const openDetails = () => {
    step.value = 'details'
  }

  const openConfirm = () => {
    if (credits.value === undefined) return
    if (!credits.value) {
      openDialog(Dialog.BuyReportCredits, {
        stack: true,
        props: { afterPurchase: () => closeAllDialogs() },
      })
      return
    }
    createError.value = null
    step.value = 'confirm'
  }

  const backToCapture = () => {
    results.value = null
    selected.value = null
    step.value = 'capture'
    if (mode.value === 'visual') void openCamera()
  }

  const back = () => {
    if (creating.value !== null) return
    if (step.value === 'confirm' || step.value === 'details') {
      step.value = 'match'
      return
    }
    if (step.value === 'match' && (results.value?.findings.length ?? 0) > 1) {
      step.value = 'list'
      return
    }
    backToCapture()
  }

  const createReport = async () => {
    const finding = selected.value
    const media = results.value?.media
    if (!finding || !media || creating.value !== null) return
    createError.value = null
    creating.value = 0
    try {
      await createCheckerReport(finding, media, (percentage) => {
        creating.value = percentage
      })
      closeAllDialogs()
    } catch (failure) {
      creating.value = null
      createError.value =
        failure instanceof Error
          ? failure.message
          : 'Something went wrong. Please try again.'
    }
  }

  const openFile = (copyrightId: string) => {
    closeAllModals()
    closeAllSheets()
    router.push(`/file/${copyrightId}`)
  }

  const subscribePcm = (listener: (pcm: Uint8Array, rate: number) => void) => {
    pcmListeners.add(listener)
    return () => {
      pcmListeners.delete(listener)
    }
  }

  const title = computed(() => {
    switch (step.value) {
      case 'empty':
      case 'list':
        return 'Flagged matches'
      case 'match':
        return 'Potential match found'
      case 'confirm':
        return 'Confirm report creation'
      case 'details':
        return 'Potential infringing work'
      default:
        return 'Infringement checker'
    }
  })

  const handleVisibility = () => {
    if (document.visibilityState === 'hidden') void check?.finish()
  }
  document.addEventListener('visibilitychange', handleVisibility)

  onBeforeUnmount(() => {
    disposed = true
    document.removeEventListener('visibilitychange', handleVisibility)
    permissionWatchers.forEach((status) => {
      status.onchange = null
    })
    check?.destroy()
    check = null
    stopTracks(audioStream)
    closeCamera()
    uploadAbort?.abort()
    results.value?.frames.forEach((url) => URL.revokeObjectURL(url))
  })

  return {
    mode,
    step,
    title,
    results,
    emptySource,
    selected,
    live,
    requesting,
    error,
    microphone,
    camera,
    cameraStream,
    upload,
    creating,
    createError,
    credits,
    details,
    localWork,
    busy,
    findingCount,
    listening,
    capturing,
    glowMode,
    sampleRate: SAMPLE_RATE,
    setMode,
    setVideo,
    toggleLive,
    review,
    pickFile,
    openMatch,
    openDetails,
    openConfirm,
    back,
    backToCapture,
    createReport,
    openFile,
    subscribePcm,
  }
}

export type Checker = ReturnType<typeof createChecker>

const CHECKER_KEY: InjectionKey<Checker> = Symbol('checker')

export const provideChecker = () => {
  const checker = createChecker()
  provide(CHECKER_KEY, checker)
  return checker
}

export const useChecker = () => {
  const checker = inject(CHECKER_KEY)
  if (!checker) throw new Error('useChecker requires provideChecker')
  return checker
}
